import { FEATURE_FLAGS } from '@mondaygpt/constants'
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { isUserMediaContent } from '@mondaygpt/types'
import { Button } from 'components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/Dialog'
import { Input } from 'components/Input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/Select'
import { AnimatePresence, motion } from 'framer-motion'
import { useAnalytics, useIsMobile } from 'hooks'
import { cn } from 'lib/utils'
import {
  CalendarOff,
  Calendar,
  Check,
  Clock,
  Download,
  ExternalLink,
  Loader2,
  Send,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { PlatformIcon, platformLabel } from 'components/PlatformIcon'
import { apiCall } from 'modules/core/services/api/api'
import { ProjectService } from 'openapi/services/ProjectService'
import { CreativeService } from 'openapi/services/CreativeService'
import { selectBusinessId } from 'modules/authentication'
import { useSubscriptionGate } from 'modules/billing'
import { collectDownloadableMedia, downloadCreativeMedia } from '../utils/download-media'
import { useAppSelector } from 'modules/core'
import { useFeatureGate, usePlatformRolloutEnabled } from 'modules/feature-flags'
import { oauthUtils } from 'modules/oauth/utils/oauth-utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { useEnsureBrandKitTimezone } from '../../common/hooks/use-ensure-brand-kit-timezone'
import { useScheduleTimezone } from '../../common/hooks/use-brand-timezone'
import {
  buildScheduledInstant,
  isTimeOptionInPast,
  splitIsoInTz,
  todayInTz,
} from '../../common/utils/business-timezone'
import {
  getAvailableTimeOptions,
  getDefaultSchedule,
} from '../../common/utils/schedule-time-options'
import { usePlatformConnections } from '../hooks/usePlatformConnections'
import { useDeriveSiblings } from '../hooks/useDeriveSiblings'
import { isSyntheticPostIdea } from '../selectors/posts-page-items'
import { creativeHasRenderableMedia, isFailedSuggestion } from '../utils/suggestion-status'
import { useLinkedInConnect } from 'modules/oauth/hooks/useLinkedInConnect'
import { approveCtaLabel } from '../utils/approve-cta-label'
import { defaultPlatformSeed, useEnsureAutopilotPlansLoaded } from '../utils/default-platform-seed'
import { type ComposerNavState } from '../pages/ComposerPage'
import { useFeedAudioStore } from '../store/feed-audio-store'
import { useCreativeProcessStore } from '../store/creative-process-store'
import { verdictHeadline } from '../process/score-explainers'
import { useRestrictedPlatforms } from '../../autopilot/hooks/useRestrictedPlatforms'
import { useIdeaStore, type LivePlatform, type PostIdea } from '../store/idea-store'
import type { Creative, TikTokPostSettings, TikTokVideoContent } from '../types'
import { TikTokPostDialog } from './TikTokPostDialog'
import {
  EMPTY_TIKTOK_POST_SETTINGS,
  isCompleteTikTokPostSettings,
} from '../utils/tiktok-post-settings'
import type { PostEntrySource } from '../utils/post-entry-source'
import {
  livePlatformToSocial,
  PLATFORM_PUBLISH_CONFIG,
  resolveSimulatePublish,
  analyticsPlatformFor,
} from '../utils/publish-creative'
import { MobileBottomSheet } from './MobileBottomSheet'
import { PostModalPlatformRow } from './PostModalPlatformRow'
import { PostModalPreviewPane } from './PostModalPreviewPane'
import {
  POST_PUBLISH_PANEL_LAYOUT_ID,
  PostPublishPanel,
  type PublishTarget,
} from './PostPublishProgressDialog'
import { PostNowMenu } from './ScheduleSplitButton'

const PLATFORM_LABEL: Record<LivePlatform, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  youtube_shorts: 'YouTube Shorts',
  pinterest: 'Pinterest',
  x: 'X',
}

/** Platform rows in candidate order, with X always last. */
export function orderPlatformRows(platforms: LivePlatform[]): LivePlatform[] {
  return [...platforms.filter(p => p !== 'x'), ...platforms.filter(p => p === 'x')]
}

function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const hour = h % 12 || 12
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`
}

// Verdicts where the judge would NOT ship the post as-is. The composer already says
// so ("Blocked by a check"), but this dialog offered a bare "Schedule for …" with no
// trace of the block — a user could schedule a post Taka itself considers not
// shippable without ever knowing (F-06). Advisory verdicts (REVISE/COMPETENT) stay
// silent: the dialog warns on hard fails only, it does not nag.
const WONT_SHIP_VERDICTS: ReadonlySet<string> = new Set(['NO_SHIP_GATE_FAIL', 'REJECT', 'TEMPLATE'])

function formatScheduleSummary(date: string, time: string): string {
  const d = new Date(`${date}T${time}:00`)
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at ${formatTime(time)}`
}

/**
 * Normalize a confirm/publish error into a Mixpanel error_type bucket and a
 * user-facing message (server message when present, else the raw error text).
 * Shared by the Schedule and Post-now submit paths.
 */
function classifyPostError(err: unknown): {
  error: Error
  errorType: 'server' | 'network' | 'validation' | 'unknown'
  message?: string
} {
  const error = err as Error & {
    response?: { status?: number; data?: { message?: string } }
    status?: number
    body?: { message?: string }
  }
  const status = error.response?.status ?? error.status
  const errorType =
    typeof status === 'number'
      ? status >= 500
        ? 'server'
        : status === 0
          ? 'network'
          : 'validation'
      : 'unknown'
  const message = error.body?.message ?? error.response?.data?.message ?? error.message
  return { error, errorType, message }
}

interface PostModalProps {
  idea: PostIdea | null
  /**
   * Fired when the modal closes. `result.scheduled` is true when the close
   * follows a successful schedule (we held a brief acid success state in
   * the CTA before closing). Callers can use this to drive their own
   * "just scheduled" celebration on the surface that opened the modal
   * (e.g. the composer's Schedule button pulse).
   */
  onClose: (result?: { scheduled: boolean }) => void
  /**
   * Override the default initial selection (primary platform). Used by the
   * IdeaCard "Retry failed" dropdown so the modal opens scoped to just the
   * platforms that need a re-publish.
   */
  initialPlatforms?: LivePlatform[]
  /**
   * When set, the modal is scoped to exactly these platforms — rows for
   * other candidates are dropped, the +/✓ badge becomes read-only, and the
   * selection set is fixed. Used by the composer entry path, where the
   * composer's platform strip is the source of truth and the user can't
   * add or remove platforms inside this modal. Takes precedence over
   * `initialPlatforms` for both row rendering and selection seeding.
   */
  lockedPlatforms?: LivePlatform[]
  /**
   * IMMEDIATE opener — feeds `home_post_modal_opened.entry_source` and
   * `home_post_modal_closed.entry_source`. Tracks where the modal was opened
   * FROM (one hop back), not the journey origin. Defaults to 'idea_card' for
   * backwards compatibility with the original Home-grid entry path.
   */
  openedFrom?: 'idea_card' | 'calendar_entry' | 'composer' | 'cockpit' | 'post_arrival'
  /**
   * JOURNEY ORIGIN — feeds `post_scheduled.entry_source`. The surface where
   * the user *started* this scheduling intent, independent of any composer
   * round-trip. Forwarded through `location.state.entry_source` across
   * PostModal ↔ Composer navigations. When omitted on Post Scheduled, the
   * event simply omits `entry_source` (it's optional).
   */
  entrySource?: PostEntrySource
  /**
   * When provided, the parent owns the connect step for the Schedule CTA.
   * PostModal calls this with the still-missing platforms and awaits the
   * final platform list to publish (resolved) or aborts the schedule
   * (rejected). When omitted, PostModal runs its inline per-platform OAuth
   * popup loop instead — that's the IdeaCard behavior.
   */
  onConnectRequired?: (missing: LivePlatform[]) => Promise<LivePlatform[]>
  /**
   * How the modal was opened. `'post_now'` (composer "Post now" caret) auto-
   * opens the Post-now confirmation on mount; `'schedule'` (default) opens the
   * normal scheduling surface. Either way the user can still pick the other
   * action from the footer.
   */
  intent?: 'schedule' | 'post_now'
}

/**
 * Per-platform preview + publish surface for Posts. Renders all candidate
 * platforms as a tab strip; the active tab swaps the right preview chrome
 * (Instagram caption+hashtags vs Facebook message vs LinkedIn long-form,
 * etc.). Selection-for-publish is independent of preview-switching — tab body
 * switches viewing, the corner badge toggles selection. See
 * `playbook/...` and the agreed plan for the full UX rationale.
 *
 * Sibling creatives derive lazily: selected platforms spawn via
 * `previewSiblings` (selection IS the publish set), and viewing a tab derives
 * just that platform on demand. No all-candidates fan-out on open — see
 * playbook/product/multi-platform/DESIGN.md (Phase 0).
 */
export function PostModal({
  idea: ideaProp,
  onClose,
  initialPlatforms,
  lockedPlatforms,
  openedFrom = 'idea_card',
  entrySource,
  onConnectRequired,
  intent = 'schedule',
}: PostModalProps) {
  // ── Live publish-result overlay ──────────────────────────────────────────
  // Some openers (the calendar) pass a FROZEN useState copy of the idea, so
  // Pusher publish results — which patch the idea-store — never reach it: the
  // open modal kept showing "failed" + Retry on a post that had just gone
  // live. Merge the store's per-creative result overlay onto the prop so all
  // downstream reads (row failure state, canRetry, isLive) see the live truth.
  // Gated to results that arrived AFTER this project was opened here: opener
  // surfaces update their entities live, so anything older is already baked
  // into the prop — and a stale overlay must never resurrect a failure that a
  // later re-schedule cleared server-side.
  const overlayGateRef = useRef<{ projectId?: string; at: number }>({
    projectId: ideaProp?.projectId,
    at: Date.now(),
  })
  if (overlayGateRef.current.projectId !== ideaProp?.projectId) {
    overlayGateRef.current = { projectId: ideaProp?.projectId, at: Date.now() }
  }
  const publishOverlay = useIdeaStore(s =>
    ideaProp?.primaryCreative ? s.publishResultsByCreative[ideaProp.primaryCreative.id] : undefined
  )
  const idea = useMemo(() => {
    if (
      !ideaProp?.primaryCreative ||
      !publishOverlay ||
      publishOverlay.terminalAt < overlayGateRef.current.at
    ) {
      return ideaProp
    }
    return {
      ...ideaProp,
      primaryCreative: {
        ...ideaProp.primaryCreative,
        publishedAt: publishOverlay.publishedAt ?? ideaProp.primaryCreative.publishedAt,
        publishFailure: publishOverlay.publishFailure ?? undefined,
      },
    }
  }, [ideaProp, publishOverlay])

  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const { gate } = useSubscriptionGate()
  const businessId = useAppSelector(selectBusinessId)
  // Capture the brand TZ on the first ConfirmPost that opens with it unset,
  // so the very same submit anchors to the correct zone.
  useEnsureBrandKitTimezone()
  // Zone every wall-clock date/time the user picks here is anchored to. Brand
  // TZ when captured, browser TZ until then. Used to (a) build the persisted
  // instant and (b) decide which time-of-day slots have already passed today.
  const scheduleTz = useScheduleTimezone()
  const restrictedPlatforms = useRestrictedPlatforms()
  const isRolloutOn = usePlatformRolloutEnabled()
  const simulatePublish = resolveSimulatePublish(useFeatureGate(FEATURE_FLAGS.simulatePublish))
  const confirmIdea = useIdeaStore(s => s.confirmIdea)
  const approve = useIdeaStore(s => s.approve)
  const unschedule = useIdeaStore(s => s.unschedule)
  const republishIdeaFailures = useIdeaStore(s => s.republishIdeaFailures)
  const siblingsState = useIdeaStore(s => (idea ? s.siblingsByProject[idea.projectId] : undefined))
  const {
    loading: connectionsLoading,
    connections,
    refetch: refetchConnections,
  } = usePlatformConnections()
  const plansLoaded = useEnsureAutopilotPlansLoaded()
  const { resolveConnectProvider, chooser } = useLinkedInConnect()

  // The judge's latest snapshot for the primary creative — the same per-creative
  // store the composer's ScorePanel reads, so the dialog and the composer can
  // never disagree about whether Taka would ship this post.
  const primaryCreativeId = idea?.primaryCreative?.id
  const fetchGeneration = useCreativeProcessStore(s => s.fetchGeneration)
  const processSnapshot = useCreativeProcessStore(s =>
    primaryCreativeId ? s.byCreative[primaryCreativeId]?.snapshot : undefined
  )
  useEffect(() => {
    if (primaryCreativeId) void fetchGeneration(primaryCreativeId)
  }, [primaryCreativeId, fetchGeneration])

  const [submitting, setSubmitting] = useState(false)
  const [approving, setApproving] = useState(false)
  const [unscheduling, setUnscheduling] = useState(false)
  const [downloadingMedia, setDownloadingMedia] = useState(false)
  // Default the date to today in the brand timezone (was UTC today, which rolls
  // a day early/late for non-UTC brands near midnight).
  const [date, setDate] = useState(() => todayInTz(scheduleTz))
  const [time, setTime] = useState('10:00')
  const [selected, setSelected] = useState<Set<LivePlatform>>(new Set())
  const [viewing, setViewing] = useState<LivePlatform | null>(null)
  const [direction, setDirection] = useState<1 | -1>(1)
  const [connectingPlatform, setConnectingPlatform] = useState<LivePlatform | null>(null)
  const lastViewingRef = useRef<LivePlatform | null>(null)
  // Tracks the projectId we've already seeded selection for. Prevents the
  // selection effect from clobbering the user's manual toggles when
  // `connections` refreshes (e.g. after an inline OAuth popup completes).
  const seededProjectIdRef = useRef<string | null>(null)

  // Open lifecycle for close-event timing + abandonment. The `confirmedRef`
  // is flipped in handleSubmit's success paths so the close fires with the
  // right `confirmed` flag regardless of which close path runs.
  const openedAtRef = useRef<number | null>(null)
  const confirmedRef = useRef(false)
  const openSnapshotRef = useRef<{ shape: string; candidate_count: number } | null>(null)

  // Phase machine — `select` is the normal selection surface; `publishing`
  // mounts the child PostPublishProgressDialog stacked on top while per-
  // platform publishes fan out. `scheduled` is a brief celebratory state
  // shown in the CTA position after confirmIdea resolves, before the modal
  // auto-closes. The PostModal stays open underneath.
  const [phase, setPhase] = useState<'select' | 'publishing' | 'scheduled'>('select')
  // ISO date string captured at the moment confirmIdea succeeds — drives
  // the success CTA copy so it survives the brief gap before close.
  const [scheduledAtForSuccess, setScheduledAtForSuccess] = useState<string | null>(null)
  // Inline error surfaced beneath the CTA when confirmIdea throws. Cleared
  // on the next submit attempt + when the modal re-opens on a new idea.
  const [scheduleError, setScheduleError] = useState<string | null>(null)
  const [publishTargets, setPublishTargets] = useState<PublishTarget[]>([])
  // Post-now confirmation gate. Posting is irreversible (unlike Schedule, which
  // has Unschedule), so the caret "Post now" routes through this confirm before
  // the publish fans out.
  const [showPostNowConfirm, setShowPostNowConfirm] = useState(false)
  // TikTok's Content Sharing Guidelines: the user picks the audience,
  // interactions and commercial disclosure per post before anything is sent.
  // When TikTok is in the publish set and its creative has no saved choices,
  // the submit path pauses on the TikTok dialog; the choices are held here
  // until the post is confirmed (the TikTok sibling may only exist after
  // confirm), then written to that creative before it publishes.
  const [tiktokPrompt, setTiktokPrompt] = useState<null | 'post_now' | 'schedule'>(null)
  const pendingTikTokSettingsRef = useRef<TikTokPostSettings | null>(null)

  // Open lifecycle for close-event timing. When idea becomes non-null, snapshot
  // open-time and shape/candidate count. When idea becomes null (modal closed
  // by any path: backdrop, escape, X, after submit), emit the close event.
  //
  // Keyed on `idea?.projectId`, NOT `idea` — so when confirmIdea updates the
  // idea-store and the parent re-renders with a new `idea` object reference
  // for the same project, this effect does NOT re-fire. Otherwise it would
  // wipe `confirmedRef = false` mid-handleSubmit and double-track the
  // HOME_POST_MODAL_OPENED event for every successful schedule.
  useEffect(() => {
    if (idea) {
      openedAtRef.current = Date.now()
      confirmedRef.current = false
      openSnapshotRef.current = {
        shape: idea.shape,
        candidate_count: idea.candidatePlatforms.length,
      }
      errorReportingService.addBreadcrumb(`post_modal_opened: ${idea.projectId}`, 'post', {
        projectId: idea.projectId,
        shape: idea.shape,
      })
      // Silence any card behind the modal that was playing with sound. The
      // modal's own preview (PreviewVideo) owns audio while open via its
      // separate preview-sound-store — without this, an unmuted feed card keeps
      // playing under the overlay and you'd hear both at once.
      useFeedAudioStore.getState().setUnmuted(null)
      return
    }
    if (openedAtRef.current !== null && openSnapshotRef.current) {
      track(AnalyticsEvents.Home.HOME_POST_MODAL_CLOSED, {
        confirmed: confirmedRef.current,
        time_open_ms: Date.now() - openedAtRef.current,
        shape: openSnapshotRef.current.shape,
        candidate_count: openSnapshotRef.current.candidate_count,
        entry_source: openedFrom,
      })
      openedAtRef.current = null
      openSnapshotRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea?.projectId, track, openedFrom])

  // Reset non-selection state whenever a NEW idea opens the modal. Selection
  // seeding happens in a separate effect below (gated on connection state);
  // sibling derivation follows selection/viewing in the effects after that.
  //
  // Keyed on `idea?.projectId`, NOT `idea` — `confirmIdea` mutates the
  // idea-store with a new object reference for the same project. Keying on
  // the object would re-run this body mid-handleSubmit, wiping the
  // `phase = 'scheduled'` celebration back to `'select'` (the user sees
  // nothing, then the modal closes when the success setTimeout fires —
  // which is exactly the symptom of the "looks like nothing happened" bug).
  useEffect(() => {
    if (!idea) {
      seededProjectIdRef.current = null
      return
    }
    // Reopening on a scheduled idea pre-fills the existing date/time so the
    // user can reschedule rather than start from a default.
    if (idea.status === 'scheduled' && idea.scheduledAt) {
      // Pre-fill the picker with the brand-TZ wall clock (was a UTC date + a
      // browser-local time — mismatched). scheduleTz is the zone the submit
      // re-anchors to, so the round-trip is lossless.
      const parts = splitIsoInTz(idea.scheduledAt, scheduleTz)
      setDate(parts.date)
      setTime(parts.time)
    } else {
      // Home-grid Posts: open pre-filled with the first slot still available
      // (today if any hours remain, else tomorrow's first slot) so the default
      // is never a time that's already passed. The post-now path was removed
      // from this modal — every regular Post is scheduled; synthetic creatives
      // (Assistant chat) still go through their immediate-publish path with
      // whenControls=null.
      const fallback = getDefaultSchedule(scheduleTz)
      setDate(fallback.date)
      setTime(fallback.time)
    }
    const firstViewing = initialPlatforms?.[0] ?? idea.primaryPlatform
    setViewing(firstViewing)
    lastViewingRef.current = firstViewing
    setSubmitting(false)
    setApproving(false)
    setUnscheduling(false)
    setPhase('select')
    setPublishTargets([])
    setScheduleError(null)

    track(AnalyticsEvents.Home.HOME_POST_MODAL_OPENED, {
      shape: idea.shape,
      candidate_count: idea.candidatePlatforms.length,
      entry_source: openedFrom,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea?.projectId, track, initialPlatforms, openedFrom])

  // Seed publish-selection once connection state has loaded.
  // The ref guard prevents this effect from clobbering the user's manual
  // toggles when `connections` later refreshes (e.g. after an inline OAuth
  // popup).
  useEffect(() => {
    if (!idea) return
    if (seededProjectIdRef.current === idea.projectId) return

    // Seed precedence: lockedPlatforms (composer entry, fixed set) →
    // initialPlatforms (retry-failures mode) → confirmedPlatforms (reopening an
    // already-confirmed post — honor the platforms it was actually scheduled
    // for, so a dropped primary isn't silently re-checked and re-added on the
    // next confirm) → defaultPlatformSeed (fresh suggestion: active plan
    // channels → connected channels, primary always included). Unconnected
    // platforms beyond the primary are never pre-selected but stay addable —
    // if the user selects one and clicks Post, the submit path runs the
    // connect flow inline before publishing. While connections/plans load,
    // the fresh-suggestion branch seeds a provisional [primary] with the ref
    // unset, upgrading once loaded — unless the user already toggled a row
    // (handleToggleSelect sets the ref), which must never be clobbered.
    const firstNonEmpty = (arr?: LivePlatform[]): LivePlatform[] | undefined =>
      arr && arr.length > 0 ? arr : undefined
    const explicit =
      firstNonEmpty(lockedPlatforms) ??
      firstNonEmpty(initialPlatforms) ??
      firstNonEmpty(idea.confirmedPlatforms)
    if (!explicit && (connectionsLoading || !plansLoaded)) {
      setSelected(prev => (prev.size === 0 ? new Set([idea.primaryPlatform]) : prev))
      return
    }
    const fallback = defaultPlatformSeed(idea, connections).filter(
      p => isRolloutOn(p) || p === idea.primaryPlatform
    )
    setSelected(new Set(explicit ?? fallback))
    seededProjectIdRef.current = idea.projectId
  }, [
    idea,
    connectionsLoading,
    connections,
    plansLoaded,
    initialPlatforms,
    lockedPlatforms,
    isRolloutOn,
  ])

  // Selection drives derivation: every selected platform (plus the viewed
  // tab, so browsing candidates still paints real per-platform copy) derives
  // lazily through the shared hook — at most one attempt per platform per
  // open, cache and in-flight dedup inside previewSiblings.
  const wantedPlatforms = useMemo(
    () => (viewing && !selected.has(viewing) ? [...selected, viewing] : [...selected]),
    [selected, viewing]
  )
  useDeriveSiblings(idea ?? undefined, wantedPlatforms)

  const handleSelectViewing = useCallback(
    (platform: LivePlatform) => {
      if (!idea) return
      const platforms = idea.candidatePlatforms
      const prev = lastViewingRef.current ?? platform
      const dir: 1 | -1 = platforms.indexOf(platform) >= platforms.indexOf(prev) ? 1 : -1
      setDirection(dir)
      setViewing(platform)
      lastViewingRef.current = platform
      track(AnalyticsEvents.Home.HOME_POST_MODAL_TAB_SWITCHED, { platform })
    },
    [idea, track]
  )

  /**
   * Toggle a platform's selection-for-publish. Pure selection — the row's +
   * is a candidate-picker only. OAuth for unconnected platforms is deferred
   * to the Schedule CTA (see handleSubmit), mirroring the Composer strip.
   */
  const handleToggleSelect = useCallback(
    (platform: LivePlatform) => {
      // Manual edit — the selection is now user-owned; the seed effect must
      // never overwrite it (relevant while connections/plans are still loading).
      if (idea) seededProjectIdRef.current = idea.projectId
      const wasSelected = selected.has(platform)
      setSelected(prev => {
        const next = new Set(prev)
        if (wasSelected) next.delete(platform)
        else next.add(platform)
        return next
      })
      track(AnalyticsEvents.Home.HOME_POST_MODAL_PLATFORM_TOGGLED, {
        platform,
        selected: !wasSelected,
      })
    },
    [idea, selected, track]
  )

  // ─── Child progress dialog wiring (hooks must run unconditionally) ──────
  // `useCallback` here so the child dialog's `onDone` / `onRetryFailures`
  // identities stay stable across publish-status updates.
  const handleProgressDone = useCallback(() => {
    setPhase('select')
    setPublishTargets([])
    onClose()
  }, [onClose])

  // "Edit in {Platform}" CTA → close the modal and route to the post-aware
  // composer for that platform. Composer is the Studio of this Post; PostModal
  // remains the publish/schedule surface only. Hand the modal's transient UI
  // state (in-flight platform selection + staged date/time) through via
  // location.state so the composer hydrates consistently with what the user
  // just set up here.
  const handleEditPlatform = useCallback(
    (platform: LivePlatform) => {
      if (!idea) return
      track(AnalyticsEvents.Home.HOME_POST_MODAL_EDIT_TAPPED, {
        platform,
        project_id: idea.projectId,
      })
      // The arrival view is a PAGE, not an overlay: its onClose pushes '/',
      // which poisons history so the composer's Back lands on Home instead of
      // back on /post/:id. The route change below unmounts it on its own.
      if (openedFrom !== 'post_arrival') onClose()
      const isSyntheticIdea = isSyntheticPostIdea(idea.projectId)
      // Always include the platform being edited in the composer's strip
      // selection. Otherwise opening the FB preview tab and clicking "Edit
      // this post" lands the user in a composer where FB isn't selected,
      // since the modal's `selected` set is seeded from confirmedPlatforms
      // (often just [primary]) and never auto-toggles on tab switch.
      const navState: ComposerNavState = {
        activePlatforms: Array.from(new Set([...selected, platform])),
        scheduledAt: isSyntheticIdea ? undefined : buildScheduledInstant(date, time, scheduleTz),
        source: 'post_modal',
        entry_source: entrySource,
      }
      // One Post model: editing lands on the POST view (the editor); the
      // staged selection still rides along via navState.
      navigate(`/compose/${idea.projectId}`, { state: navState })
    },
    [idea, navigate, onClose, track, selected, date, time, scheduleTz, entrySource, openedFrom]
  )

  // Arrival-view "New take": regenerate the draft in place. The store swaps the
  // primary for a fresh generating creative, so the preview flips back to the
  // narrated skeleton and the new take streams in over the same modal.
  const regenerateIdea = useIdeaStore(s => s.regenerateIdea)
  const canNewTake =
    openedFrom === 'post_arrival' &&
    !!idea &&
    idea.status === 'draft' &&
    !idea.isRegenerating &&
    !idea.primaryCreative?.scheduledAt &&
    !isSyntheticPostIdea(idea.projectId) &&
    // Bring-your-own-content: the user's upload publishes untouched, so a
    // regenerate-style "New take" would violate the contract. Gate it off.
    !isUserMediaContent(idea.primaryCreative?.content as Record<string, unknown> | null | undefined)
  const handleNewTake = useCallback(() => {
    if (!idea) return
    track(AnalyticsEvents.Home.HOME_IDEA_CARD_REGENERATE_TAPPED, {
      idea_id: idea.projectId,
      shape: idea.shape,
      source: 'post_arrival',
    })
    void regenerateIdea(idea.projectId)
  }, [idea, regenerateIdea, track])

  const handleProgressRetry = useCallback(
    (failed: LivePlatform[]) => {
      if (!idea) return
      republishIdeaFailures(idea.projectId)
      setSelected(new Set(failed))
      setPhase('select')
      setPublishTargets([])
    },
    [idea, republishIdeaFailures]
  )

  // Facebook's organic Page-feed carousel accepts photos only — videos can't
  // be mixed in. Disable the FB toggle (with tooltip) when the primary
  // carousel contains a video slide so the user can't queue a publish that
  // the server will reject. Computed pre-guard so the auto-deselect effect
  // below stays in-order with the other hooks.
  const primaryMediaItems = (
    idea?.primaryCreative?.content as
      { mediaItems?: Array<{ type: 'image' | 'video' }> } | null | undefined
  )?.mediaItems
  const carouselHasVideoSlide =
    idea?.shape === 'carousel' &&
    Array.isArray(primaryMediaItems) &&
    primaryMediaItems.length >= 2 &&
    primaryMediaItems.some(item => item.type === 'video')

  // Auto-clear FB from the publish list when a video slide gets added after
  // the modal seeded with FB selected — keeps state in sync with the disabled
  // toggle, otherwise the user is stuck with a ✓ they can't remove.
  useEffect(() => {
    if (!carouselHasVideoSlide) return
    setSelected(prev => {
      if (!prev.has('facebook')) return prev
      const next = new Set(prev)
      next.delete('facebook')
      return next
    })
  }, [carouselHasVideoSlide])

  // Time-of-day slots still selectable for the chosen day — past hours are
  // dropped when `date` is today (in the schedule TZ) so we never offer a slot
  // that has already passed. Recomputed when the day or zone changes.
  const availableTimeOptions = useMemo(
    () => getAvailableTimeOptions(date, scheduleTz),
    [date, scheduleTz]
  )

  // Snap the selected time forward only when it's actually in the past — e.g.
  // the date was switched to today, or the modal reopened on a stale pick. We
  // intentionally do NOT snap a future time that merely isn't on the grid (a
  // post rescheduled to 17:30 in a half-hour-offset zone stays 17:30). When the
  // whole day is gone, `availableTimeOptions` is empty and the submit CTA guards
  // against scheduling (see `submitLabel`). Note: this fires on date/zone/option
  // changes, not on the wall clock advancing while the modal sits open.
  useEffect(() => {
    if (availableTimeOptions.length === 0) return
    if (isTimeOptionInPast(date, time, scheduleTz)) {
      setTime(availableTimeOptions[0])
    }
  }, [availableTimeOptions, time, date, scheduleTz])

  // Composer "Post now" entry — auto-open the confirm once on mount. Only for
  // an actionable multi-platform Post; live/publishing, awaiting-approval, and
  // synthetic ideas have no post-now-via-confirm path here. Keyed on projectId
  // + intent so it fires once per open and never re-opens after a cancel.
  useEffect(() => {
    if (!idea || intent !== 'post_now') return
    const isReadOnlyIdea = idea.status === 'live' || idea.status === 'publishing'
    const needsApprovalIdea =
      !!idea.requiresApproval && !idea.approvedAt && !!idea.scheduledAt && !isReadOnlyIdea
    if (isReadOnlyIdea || needsApprovalIdea || isSyntheticPostIdea(idea.projectId)) return
    setShowPostNowConfirm(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea?.projectId, intent])

  // Mobile strip: with X trailing the list, a selected X can sit past the
  // viewport edge on open. Bring the first selected chip into view once per
  // open (the strip only exists on mobile; the ref is null on desktop).
  const mobileStripRef = useRef<HTMLDivElement>(null)
  const selectedKey = Array.from(selected).join(',')
  useEffect(() => {
    const el = mobileStripRef.current?.querySelector<HTMLElement>('[data-selected="true"]')
    el?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
  }, [selectedKey, isMobile])

  // Creatives currently mid-publish (retry-now or an autopilot sweep) — driven
  // by Pusher (CreativePublishStarted/Complete), so a failed row flips to a
  // spinner then live/failed in place without a reopen.
  const publishingCreativeIds = useIdeaStore(s => s.publishingCreativeIds)
  const markCreativePublishing = useIdeaStore(s => s.markCreativePublishing)

  if (!idea) return null

  const viewingCreative: Creative | null = viewing
    ? viewing === idea.primaryPlatform
      ? idea.primaryCreative
      : (siblingsState?.byPlatform[viewing] ?? null)
    : null

  const isSynthetic = isSyntheticPostIdea(idea.projectId)
  // The slot this post ALREADY holds, as brand-TZ wall-clock parts. One
  // derivation feeds the CTA ("Scheduled for …" / "Reschedule for …") and the
  // Post now confirmation. Null for drafts and synthetic ideas.
  const committedSlot =
    !isSynthetic && idea.status === 'scheduled' && idea.scheduledAt
      ? splitIsoInTz(idea.scheduledAt, scheduleTz)
      : null
  // The committed slot while it is still ahead of us: what Post now would skip.
  // Also drives the `overrides_schedule` telemetry on Social Publish Tapped.
  const existingSchedule =
    committedSlot && idea.scheduledAt && new Date(idea.scheduledAt).getTime() > Date.now()
      ? formatScheduleSummary(committedSlot.date, committedSlot.time)
      : null

  // Read-only states: a live post (already published) or one mid-publish. The
  // modal hides the scheduling controls + edit affordances and swaps the CTA
  // for the live link(s) / a publishing note. Live = "links only" per product.
  //
  // "Live" is ANY platform published — a partially-published post (one platform
  // live, others failed/pending) reads as live too, so the modal is view-only.
  // You never reschedule a post that's already partly out; recovery of the
  // failed platforms happens through the failed chip's "Retry now". The derived
  // `status === 'live'` can miss this when confirmedPlatforms/creativeType
  // metadata is stale, so we also check the creatives' `publishedAt` directly
  // (ground truth: primary + any harvested sibling).
  const anyCreativePublished =
    !!idea.primaryCreative?.publishedAt ||
    Object.values(siblingsState?.byPlatform ?? {}).some(c => !!c?.publishedAt)
  const isLive = idea.status === 'live' || anyCreativePublished
  const isPublishing = idea.status === 'publishing'
  // A stamped publishFailure marker means a publish was ATTEMPTED — the post is
  // past scheduling. Showing the schedule flow (date picker + "Scheduled for…")
  // next to failure alerts reads as a contradiction, so a failed post gets the
  // same read-only treatment as a live one: recovery is the per-row Retry /
  // Reconnect, not a re-schedule. Markers only — a provider restriction
  // (`account_restricted` derived at read time) is a PRE-publish warning and
  // must keep the schedule flow.
  const anyPublishFailed =
    !!idea.primaryCreative?.publishFailure ||
    Object.values(siblingsState?.byPlatform ?? {}).some(c => !!c?.publishFailure)
  const isReadOnly = isLive || isPublishing || anyPublishFailed
  // Generation readiness gate: a post whose generation terminally FAILED, or that
  // is still being made (no renderable media yet), has nothing to schedule. The
  // arrival modal previously offered a live "Schedule for …" on exactly these,
  // inviting the user to schedule a post that doesn't exist. Block the submit and
  // say so; "New take" (Try again) stays available for the failed case. Scoped to
  // non-read-only drafts — a live/published post always has media.
  const generationFailed = !isReadOnly && isFailedSuggestion(idea.primaryCreative)
  const generationPending =
    !isReadOnly && !generationFailed && !creativeHasRenderableMedia(idea.primaryCreative)
  const generationNotReady = generationFailed || generationPending
  // Header names the post's condition — "Post" alone undersells a post that's
  // already out (or failed to get out). Mixed outcome (some platforms live,
  // some failed) says so: "Live post" would oversell it, "Failed post" would
  // undersell it.
  const modalTitle = isLive
    ? anyPublishFailed
      ? 'Partly live'
      : 'Live post'
    : isPublishing
      ? 'Publishing…'
      : anyPublishFailed
        ? 'Failed post'
        : 'Post'

  // Per-platform "Retry now": re-publish just this platform's failed creative
  // through the server processor. Fire-and-forget — the request only enqueues
  // (returns instantly); the card/modal reflect publishing → live/failed live
  // via Pusher. No client OAuth: an unconnected platform simply fails again.
  const handleRetryPlatform = (platform: LivePlatform, creativeId: string) => {
    // Optimistically flag it publishing so the row flips to a spinner
    // immediately; Pusher Started/Complete then own the lifecycle. On enqueue
    // error, un-flag so the failed state comes back.
    markCreativePublishing(creativeId, true)
    apiCall(ProjectService.projectControllerRetryNowV1, null, { creativeIds: [creativeId] })
      .then(() => toast.success(`Retrying ${platformLabel(platform)}…`))
      .catch(err => {
        // retry-now re-validates content server-side, so a post that failed for
        // an over-cap caption is refused here with the reason rather than
        // re-queued to fail again. Relay it: the platform name alone tells the
        // user nothing they can act on.
        console.error('Retry failed:', err)
        const { message } = classifyPostError(err)
        toast.error(message ?? `Couldn't retry ${platformLabel(platform)}`)
        markCreativePublishing(creativeId, false)
      })
  }

  // Review-mode post still awaiting approval — surface an acid Approve CTA in
  // the footer (the card no longer carries one). Derived the same way as the
  // old IdeaCard footer: requiresApproval && !approvedAt.
  // A review-mode post needs approval while it holds a slot and is not out
  // yet. The idea-store only derives 'scheduled' from a FUTURE slot, so a
  // post whose slot already passed reads 'draft'; gating on the slot itself
  // keeps the Approve CTA (and the server's missed-slot recovery) for it.
  const needsApproval =
    !!idea.requiresApproval &&
    !idea.approvedAt &&
    !!idea.scheduledAt &&
    idea.status !== 'live' &&
    idea.status !== 'publishing'

  // Honest block notice: the judge failed this post on a hard check, so say so
  // right where the user is about to schedule it. Informative, not blocking —
  // the schedule stays theirs to make (the composer's "Edit this post" is the fix
  // path). Hidden on live/publishing posts (nothing left to decide there).
  // Reels are excluded from the TEMPLATE arm: the video pipeline has no ship
  // floor yet, so a TEMPLATE reel verdict is advisory — claiming "Taka wouldn't
  // publish this" for it would overstate the judge. Carousels DO floor on it.
  const isReelShape = idea.shape === 'short_vertical_video'
  const wontShipVerdict =
    !isReadOnly &&
    processSnapshot?.verdict &&
    WONT_SHIP_VERDICTS.has(processSnapshot.verdict) &&
    !(isReelShape && processSnapshot.verdict === 'TEMPLATE')
      ? processSnapshot.verdict
      : null
  const blockedNotice = generationFailed ? (
    <div
      role="status"
      className="rounded-[8px] border border-line-bright bg-bg-3 px-3 py-2 text-[12px]"
    >
      <span className="font-medium text-[color:var(--alert-error-text)]">
        This one didn't come out.
      </span>{' '}
      <span className="text-paper-dim">Your credit's back. Try a new take, or edit it.</span>
    </div>
  ) : generationPending ? (
    <div
      role="status"
      className="rounded-[8px] border border-line-bright bg-bg-3 px-3 py-2 text-[12px]"
    >
      <span className="text-paper-dim">
        Still creating this post. You'll be able to schedule it the moment it's ready.
      </span>
    </div>
  ) : wontShipVerdict ? (
    <div
      role="status"
      className="rounded-[8px] border border-line-bright bg-bg-3 px-3 py-2 text-[12px]"
    >
      <span className="font-medium text-[color:var(--alert-error-text)]">
        {verdictHeadline(wontShipVerdict)}
        {typeof processSnapshot?.score === 'number' ? ` · scored ${processSnapshot.score}/100` : ''}
      </span>{' '}
      <span className="text-paper-dim">
        Taka wouldn't publish this as is. Edit it first, or continue anyway.
      </span>
    </div>
  ) : null

  // Single "Approve · {date}" action for a review-mode post. ONE server
  // transaction carries both the platform selection and the slot: selected
  // candidate previews become real scheduled siblings, confirmedPlatforms is
  // written, deselected previews are pruned, approvedAt lands. (Before, the
  // selection only reached the server when the user changed the time; an
  // unchanged time stamped approval alone, so a ticked Facebook row stayed a
  // preview with no slot and went silently dark.) The connect gate runs
  // first, exactly like Schedule, so an unconnected platform is connected now
  // or dropped with a toast, never scheduled into a certain "not connected"
  // failure. Close on success — the surface behind reflects the approved post
  // (the modal's `idea` is a snapshot).
  const handleApproveSubmit = async () => {
    if (approving || submitting || selected.size === 0) return
    setApproving(true)
    setScheduleError(null)
    try {
      const platforms = await resolvePlatformsForPublish()
      if (!platforms) {
        setApproving(false)
        return
      }
      // Send the slot only when the user actually moved it. An untouched
      // approve keeps every platform's own slot (a per-platform reschedule
      // made elsewhere survives); a picked time is stamped on all of them.
      const scheduledIso = buildScheduledInstant(date, time, scheduleTz)
      const slotChanged =
        !idea.scheduledAt ||
        new Date(scheduledIso).getTime() !== new Date(idea.scheduledAt).getTime()
      await approve(idea.projectId, {
        platforms,
        ...(slotChanged && { scheduledAt: scheduledIso }),
      })
      confirmedRef.current = true
      track(AnalyticsEvents.Posts.POST_APPROVED, {
        surface:
          openedFrom === 'cockpit'
            ? 'cockpit'
            : openedFrom === 'calendar_entry'
              ? 'calendar'
              : openedFrom === 'composer'
                ? 'composer'
                : 'home',
        creative_id: idea.primaryCreative?.id,
        project_id: idea.projectId,
        platform_count: platforms.length,
      })
      onClose({ scheduled: true })
    } catch (err) {
      // Surface the server's own words (an Instagram carousel verdict, "already
      // published", an incompatible platform): the approve now runs the full
      // confirm, and a generic "try again" hides a 400 that can never pass.
      const { error, message } = classifyPostError(err)
      errorReportingService.captureException(error, {
        feature: 'post_modal_approve',
        projectId: idea.projectId,
      })
      setScheduleError(message ?? "We couldn't approve this post. Please try again.")
    } finally {
      setApproving(false)
    }
  }

  // Move a scheduled post back to a draft. Reversible (no confirm), so this is a
  // direct action: clear the schedule, then close. The unscheduled post leaves
  // the scheduled views and lists under /posts as a draft. POST_UNSCHEDULED is
  // emitted server-side on the shared endpoint.
  const handleUnschedule = async () => {
    if (unscheduling || submitting || approving) return
    setUnscheduling(true)
    setScheduleError(null)
    try {
      await unschedule(idea.projectId)
      onClose()
      toast.success('Moved to draft. Find it in Posts.')
    } catch (err) {
      errorReportingService.captureException(err as Error, {
        feature: 'post_modal_unschedule',
        projectId: idea.projectId,
      })
      setScheduleError("We couldn't move this post to draft. Please try again.")
      setUnscheduling(false)
    }
  }

  // Shared preamble for Schedule, Post-now and Approve: fire the click-time
  // SOCIAL_PUBLISH_TAPPED telemetry, then run the connect-on-submit OAuth gate.
  // Returns the final platform list to act on, or null to abort (the user
  // cancelled the gate, or every selected platform was dropped). Does NOT touch
  // `submitting` — callers own that flag.
  /** The creative that will publish to TikTok (primary or sibling), if it exists yet. */
  const tiktokCreative = (): Creative | null | undefined =>
    idea.primaryPlatform === 'tiktok' ? idea.primaryCreative : siblingsState?.byPlatform.tiktok

  /** The content the TikTok dialog previews: the TikTok creative's, else the primary's (shared video). */
  const tiktokDialogContent = (): TikTokVideoContent =>
    ((tiktokCreative() ?? idea.primaryCreative)?.content ?? {}) as TikTokVideoContent

  /** True when TikTok is being published and no valid per-post choices exist yet. */
  const tiktokNeedsSettings = (platforms: LivePlatform[]): boolean => {
    if (!platforms.includes('tiktok') || pendingTikTokSettingsRef.current) return false
    const saved = (tiktokCreative()?.content as TikTokVideoContent | undefined)?.postSettings
    return !isCompleteTikTokPostSettings({ ...EMPTY_TIKTOK_POST_SETTINGS, ...(saved ?? {}) })
  }

  /** Write the held TikTok choices onto the creative that will publish to TikTok. */
  const persistPendingTikTokSettings = async (creativeId: string | undefined) => {
    const settings = pendingTikTokSettingsRef.current
    if (!settings || !creativeId) return
    // The server merges `data.content` key by key, so this touches only postSettings.
    await apiCall(CreativeService.creativeControllerUpdateV1, null, creativeId, {
      data: { content: { postSettings: settings } },
    })
    pendingTikTokSettingsRef.current = null
  }

  const resolvePlatformsForPublish = async (
    flow: { postNow?: boolean; silent?: boolean } = {}
  ): Promise<LivePlatform[] | null> => {
    // Click-time publish telemetry — fires the same SOCIAL_PUBLISH_TAPPED
    // event ComposerHeader and CreativeGenerationCard already use, so the
    // home-surface publish funnel (tap → triggered → success/fail) closes
    // and joins cleanly with the other surfaces. One event per selected
    // social platform; livePlatformToSocial returning null skips anything
    // that isn't a social platform. `overrides_schedule` marks a Post now on
    // a post that still had a future slot (the early-publish class).
    const overridesSchedule = !!flow.postNow && !!existingSchedule
    for (const livePlatform of selected) {
      const social = livePlatformToSocial(livePlatform)
      // `silent`: a re-entry after the TikTok dialog — the tap was already tracked.
      if (!social || flow.silent) continue
      track(AnalyticsEvents.Social.SOCIAL_PUBLISH_TAPPED, {
        platform: analyticsPlatformFor(social),
        creative_id: idea.primaryCreative?.id,
        shape: idea.shape,
        source: 'home',
        ...(overridesSchedule && { overrides_schedule: true }),
      })
    }

    // Connect-on-submit: if any selected platform requires OAuth and isn't
    // live, run the connect flow before publishing. Two modes:
    //   1. Parent-delegated (composer entry path): caller supplies
    //      `onConnectRequired` — we hand off the missing list, await the
    //      final platform set, and proceed (or abort if the parent rejects).
    //   2. Inline (default / IdeaCard entry path): per-platform popup loop.
    //      Cancelled/failed popups drop the platform from the publish set
    //      rather than aborting the whole post.
    let platforms = Array.from(selected)
    if (!simulatePublish && businessId) {
      const needAuth = platforms.filter(p => {
        const conn = connections[p]
        return !!conn?.needsConnection && !conn.connected && conn.oauthProvider
      })
      if (needAuth.length > 0) {
        track(AnalyticsEvents.Home.HOME_POST_MODAL_OAUTH_GATE_SHOWN, {
          platforms: needAuth,
          project_id: idea.projectId,
        })

        if (onConnectRequired) {
          // Parent owns the UX. Resolves with the final platform list,
          // rejects to abort the schedule (dialog closed, user cancelled).
          try {
            const finalPlatforms = await onConnectRequired(needAuth)
            await refetchConnections()
            platforms = finalPlatforms
            track(AnalyticsEvents.Home.HOME_POST_MODAL_OAUTH_GATE_RESOLVED, {
              completed: finalPlatforms.filter(p => needAuth.includes(p)),
              dismissed: needAuth.filter(p => !finalPlatforms.includes(p)),
              proceeded_with_count: finalPlatforms.length,
              project_id: idea.projectId,
            })
            if (platforms.length === 0) return null
          } catch {
            return null
          }
        } else {
          const dropped = new Set<LivePlatform>()
          const completed: LivePlatform[] = []
          for (const platform of needAuth) {
            const conn = connections[platform]
            if (!conn?.oauthProvider) continue
            // LinkedIn fronts two OAuth apps (personal `linkedin` / company
            // `linkedin_org`). Open the shared chooser first (when gated on);
            // a dismissal (null) drops just this platform, same as a failed
            // connect. Other providers pass through. Both LinkedIn variants land
            // on the single `linkedin` connection.
            const oauthProvider = await resolveConnectProvider(conn.oauthProvider)
            if (!oauthProvider) {
              dropped.add(platform)
              continue
            }
            track(AnalyticsEvents.Home.HOME_PLATFORM_CONNECT_TAPPED, { platform })
            setConnectingPlatform(platform)
            try {
              const result = await oauthUtils.openOAuthPopup(oauthProvider, {
                businessId,
              })
              if (result === true) {
                completed.push(platform)
              } else {
                dropped.add(platform)
              }
            } catch (err) {
              // OAuth popup threw — capture context (dismissed / blocker / provider
              // failure all surface here). Today this silently drops the platform.
              errorReportingService.captureException(err as Error, {
                feature: 'post_modal_oauth_gate',
                platform,
                projectId: idea.projectId,
                businessId,
              })
              dropped.add(platform)
            } finally {
              setConnectingPlatform(null)
            }
          }
          await refetchConnections()
          const remainingPlatforms = platforms.filter(p => !dropped.has(p))
          track(AnalyticsEvents.Home.HOME_POST_MODAL_OAUTH_GATE_RESOLVED, {
            completed,
            dismissed: Array.from(dropped),
            proceeded_with_count: remainingPlatforms.length,
            project_id: idea.projectId,
          })
          if (dropped.size > 0) {
            setSelected(prev => {
              const next = new Set(prev)
              for (const p of dropped) next.delete(p)
              return next
            })
            platforms = remainingPlatforms
            // Say WHY the platform vanished. Silently unselecting after an
            // abandoned OAuth left the user with either a dead "Select a
            // platform" button or a post that quietly went out without the
            // platform they picked. The copy must match which one happened.
            const names = Array.from(dropped)
              .map(p => PLATFORM_LABEL[p] ?? p)
              .join(', ')
            const verb = dropped.size > 1 ? "weren't" : "wasn't"
            toast.info(
              remainingPlatforms.length > 0
                ? `${names} ${verb} connected, so this post goes out without it.`
                : `${names} ${verb} connected. Tap the platform to try again.`
            )
          }
          if (platforms.length === 0) return null
        }
      }
    }

    return platforms
  }

  const handleSubmit = async (opts: { reentry?: boolean } = {}) => {
    if (submitting || selected.size === 0 || phase !== 'select') return
    setSubmitting(true)
    // Clear any prior failure surfaced from a previous attempt.
    setScheduleError(null)

    const platforms = await resolvePlatformsForPublish({ silent: opts.reentry })
    if (!platforms) {
      setSubmitting(false)
      return
    }
    // TikTok needs the user's per-post choices before it can be scheduled
    // (the sweep publishes unattended). Pause on the dialog; its CTA re-enters.
    if (tiktokNeedsSettings(platforms)) {
      setTiktokPrompt('schedule')
      setSubmitting(false)
      return
    }

    // Snapshot connection state + lead time at the moment of confirmation.
    // Powers per-platform funnels via the unified POST_SCHEDULED event.
    const connectedPlatforms = (Object.keys(connections) as LivePlatform[]).filter(
      p => connections[p]?.connected
    )
    // Persist the picked wall-clock date+time anchored to the brand's
    // timezone (falling back to the browser TZ until the brand kit captures
    // one). The previous `${date}T${time}:00Z` form stamped Z onto the local
    // wall clock, so "9am tomorrow" for a NYC user landed at 5am EDT.
    const scheduledIso = buildScheduledInstant(date, time, scheduleTz)
    const leadTimeMinutes = isSynthetic
      ? null
      : Math.max(0, Math.round((new Date(scheduledIso).getTime() - Date.now()) / 60_000))
    track(AnalyticsEvents.Posts.POST_SCHEDULED, {
      source: 'post_modal',
      project_id: idea.projectId,
      creative_id: idea.primaryCreative?.id,
      autopilot_id: idea.primaryCreative?.autopilotId ?? undefined,
      platforms,
      platform_count: platforms.length,
      shape: idea.shape,
      scheduled_at: scheduledIso,
      lead_time_minutes: leadTimeMinutes ?? 0,
      is_immediate: isSynthetic,
      connected_platforms: connectedPlatforms,
      entry_source: entrySource,
      via_composer: openedFrom === 'composer',
    })
    // Mark confirmed so the close-effect reports confirmed=true. Set here
    // (not after the await) so the synthetic path also flips it before its
    // setPhase('publishing'), which is the moment of "the user committed".
    confirmedRef.current = true

    // ─── Synthetic (standalone single-platform creative) — skip the
    // project-confirm round trip. There are no siblings to spawn; the publish
    // pipeline runs directly against the creative. Schedule is hidden for
    // these in V1 — a post-now path is enough for the Assistant Chat / Cockpit
    // creatives we're surfacing.
    if (isSynthetic) {
      const targets: PublishTarget[] = platforms.map(platform => {
        const channel = livePlatformToSocial(platform)
        const isAsync = channel ? PLATFORM_PUBLISH_CONFIG[channel].isAsync : false
        return {
          platform,
          creativeId: idea.primaryCreative?.id ?? '',
          isAsync,
        }
      })
      setPublishTargets(targets)
      setPhase('publishing')
      setSubmitting(false)
      return
    }

    // ─── Schedule — confirm + close. Stays on the current page; the
    // IdeaCard flips to a "Scheduled for X" pill via idea-store.confirmIdea.
    // The post-now path was removed from this modal — every regular Post is
    // scheduled. Use the composer's publish button for immediate publishing.
    const scheduledAt = scheduledIso
    try {
      await confirmIdea(idea.projectId, platforms, { scheduledAt })
      // The TikTok sibling exists now — give it the held choices before the
      // sweep ever looks at it.
      const tiktokAfterConfirm =
        idea.primaryPlatform === 'tiktok'
          ? idea.primaryCreative
          : useIdeaStore.getState().siblingsByProject[idea.projectId]?.byPlatform.tiktok
      await persistPendingTikTokSettings(tiktokAfterConfirm?.id)
    } catch (err) {
      const { error, errorType, message } = classifyPostError(err)
      track(AnalyticsEvents.Home.HOME_POST_MODAL_SCHEDULE_FAILED, {
        error_type: errorType,
        error_message: error?.message,
        platforms,
        scheduled: true,
      })
      errorReportingService.captureException(error, {
        feature: 'post_modal_confirm',
        projectId: idea.projectId,
        platforms,
        scheduledAt,
      })
      // Surface the server's message when we have one (e.g. "Cannot confirm
      // Post in status ..."), otherwise a generic copy. Keep the modal
      // open + leave selection intact so the user can retry.
      setScheduleError(message ?? 'Could not schedule. Please try again.')
      // Flip confirmed back to false — we tracked the intent but the
      // schedule did not happen, and the close-event shouldn't claim success.
      confirmedRef.current = false
      setSubmitting(false)
      return
    }
    // Brief celebratory state in the CTA position — the button morphs to
    // acid + "Scheduled for {date}" for ~1.2s before the modal auto-closes.
    // The close emits `{ scheduled: true }` so the opener (e.g. composer)
    // can run its own celebration on the surface that launched the modal.
    setSubmitting(false)
    setScheduledAtForSuccess(scheduledAt)
    setPhase('scheduled')
    setTimeout(() => {
      onClose({ scheduled: true })
    }, 1200)
  }

  // ─── Post now — publish immediately across the selected platforms. Reuses
  // the same connect gate as Schedule, then confirms with `postNow: true` (the
  // server clears the slot first; see the early-publish guard in
  // PublishLifecycleService.loadAndValidate) and the `keepInList` flow so
  // idea-store seeds per-platform publish targets and flips the idea to
  // 'publishing'. We then drive the morphing PostPublishPanel exactly like the
  // synthetic path. No POST_SCHEDULED here — Post now lives in the
  // SOCIAL_PUBLISH_TAPPED → TRIGGERED → SUCCESS/FAILED funnel instead.
  const handlePostNow = async (opts: { reentry?: boolean } = {}) => {
    if (submitting || selected.size === 0 || phase !== 'select') return
    setSubmitting(true)
    setScheduleError(null)

    const platforms = await resolvePlatformsForPublish({ postNow: true, silent: opts.reentry })
    if (!platforms) {
      setSubmitting(false)
      return
    }
    // TikTok's per-post choices come first (the connect gate above has just
    // made sure the account is linked, so creator_info can load).
    if (tiktokNeedsSettings(platforms)) {
      setTiktokPrompt('post_now')
      setSubmitting(false)
      return
    }
    // The user committed — mark confirmed so the close-effect reports it.
    confirmedRef.current = true

    try {
      await confirmIdea(idea.projectId, platforms, { postNow: true }, { keepInList: true })
    } catch (err) {
      const { error, errorType, message } = classifyPostError(err)
      track(AnalyticsEvents.Home.HOME_POST_MODAL_SCHEDULE_FAILED, {
        error_type: errorType,
        error_message: error?.message,
        platforms,
        scheduled: false,
      })
      errorReportingService.captureException(error, {
        feature: 'post_modal_post_now',
        projectId: idea.projectId,
        platforms,
      })
      setScheduleError(message ?? 'Could not post. Please try again.')
      confirmedRef.current = false
      setSubmitting(false)
      return
    }

    // Build publish targets from the freshly-seeded platformResults — the
    // keepInList branch resolved the creativeId per platform in the store
    // (primary creative for the primary platform, sibling otherwise).
    const seeded =
      useIdeaStore.getState().ideas.find(i => i.projectId === idea.projectId)?.platformResults ?? {}
    // The held TikTok choices land on the creative that is about to publish.
    try {
      await persistPendingTikTokSettings(seeded.tiktok?.creativeId)
    } catch (err) {
      const { message } = classifyPostError(err)
      errorReportingService.captureException(err instanceof Error ? err : new Error(String(err)), {
        feature: 'post_modal_tiktok_settings',
        projectId: idea.projectId,
      })
      setScheduleError(message ?? 'Could not save the TikTok settings. Please try again.')
      confirmedRef.current = false
      setSubmitting(false)
      return
    }
    const targets: PublishTarget[] = platforms.map(platform => {
      const channel = livePlatformToSocial(platform)
      const isAsync = channel ? PLATFORM_PUBLISH_CONFIG[channel].isAsync : false
      return {
        platform,
        creativeId: seeded[platform]?.creativeId ?? '',
        isAsync,
      }
    })
    setPublishTargets(targets)
    setPhase('publishing')
    setSubmitting(false)
  }

  // Reopening an already-scheduled post: reflect its state in the CTA rather
  // than always reading "Schedule for …". `scheduledParts` is the committed
  // wall-clock; when the picked date/time still matches it the post is
  // unchanged → "Scheduled for …", once the user edits it → "Reschedule for …".
  // (needsApproval posts use the separate Approve CTA, so they're excluded.)
  const scheduledParts = needsApproval ? null : committedSlot
  const isUnchangedSchedule =
    !!scheduledParts && scheduledParts.date === date && scheduledParts.time === time

  const submitLabel = (() => {
    if (phase === 'publishing') return 'Publishing…'
    if (submitting) {
      if (connectingPlatform) return `Connecting ${PLATFORM_LABEL[connectingPlatform]}…`
      return isSynthetic ? 'Posting…' : 'Scheduling…'
    }
    if (selected.size === 0) return 'Select a platform'
    if (isSynthetic) {
      if (selected.size === 1) {
        const only = Array.from(selected)[0]
        return `Post to ${PLATFORM_LABEL[only] ?? only}`
      }
      return `Post to ${selected.size} platforms`
    }
    if (availableTimeOptions.length === 0) return 'No times left, pick another day'
    if (isUnchangedSchedule) return `Scheduled for ${formatScheduleSummary(date, time)}`
    if (scheduledParts) return `Reschedule for ${formatScheduleSummary(date, time)}`
    return `Schedule for ${formatScheduleSummary(date, time)}`
  })()

  // ─── Reusable building blocks shared between mobile + desktop ─────────
  // When `lockedPlatforms` is set, scope the row list to exactly that set —
  // the composer entry path treats the strip as the source of truth and rows
  // for other candidate platforms shouldn't appear.
  // On a read-only post (live/failed/publishing) the platform set can't grow,
  // so uninvolved candidates are noise — scope the list to platforms the post
  // actually touches: selected for publish, published, failed, or in flight.
  // Plain consts, no useMemo: hooks can't live below the `!idea` early return,
  // and the derivation is a single pass over ≤6 candidates.
  const publishingIdSet = new Set(publishingCreativeIds)
  const involvedPlatforms = isReadOnly
    ? idea.candidatePlatforms.filter(p => {
        const c = p === idea.primaryPlatform ? idea.primaryCreative : siblingsState?.byPlatform[p]
        return (
          selected.has(p) ||
          !!c?.publishedAt ||
          !!c?.publishFailure ||
          (!!c?.id && publishingIdSet.has(c.id))
        )
      })
    : null
  // X always trails the list (Matan, 2026-09-07): it is a "coming soon" platform
  // promoted only for posts created for it, and it must not lead the rows.
  // TikTok / YouTube / Pinterest / X stay off the addable list while their
  // rollout flag is off — same gate as /integrations. A post whose primary
  // (or confirmed set) already is that platform still shows the row.
  const rolloutCandidates = idea.candidatePlatforms.filter(
    p => isRolloutOn(p) || p === idea.primaryPlatform || !!idea.confirmedPlatforms?.includes(p)
  )
  const visiblePlatforms = orderPlatformRows(
    lockedPlatforms && lockedPlatforms.length > 0
      ? idea.candidatePlatforms.filter(p => lockedPlatforms.includes(p))
      : (involvedPlatforms ?? rolloutCandidates)
  )
  const isLocked = !!(lockedPlatforms && lockedPlatforms.length > 0)
  const platformRows = visiblePlatforms.map(platform => {
    const disabledReason =
      platform === 'facebook' && carouselHasVideoSlide
        ? 'Facebook carousels support images only. Remove video slides to enable Facebook.'
        : undefined
    // Per-platform failure derivation. Priority:
    //   1. Restriction hold on the account (composition rule from the plan —
    //      restrictions render through the SAME row alert as failures, with
    //      the reason 'account_restricted').
    //   2. Sibling creative's `publishFailure.reason` from extractSiblings.
    //   3. Primary creative's own `publishFailure.reason` when this row IS the
    //      primary. NOTE: extractSiblings keys on seedFromCreativeId, which a
    //      known prod bug (`project_sibling_reuse_broken`) sometimes drops —
    //      when that happens the sibling map is missing entries, but the
    //      primary path here still surfaces the primary's own marker so the
    //      most important row never silently loses its failure treatment.
    const rowCreative =
      platform === idea?.primaryPlatform
        ? idea?.primaryCreative
        : siblingsState?.byPlatform[platform]
    const rowFailure: { reason: string } | undefined = restrictedPlatforms.includes(platform)
      ? { reason: 'account_restricted' }
      : rowCreative?.publishFailure?.reason
        ? { reason: rowCreative.publishFailure.reason }
        : undefined
    // While a creative is mid-publish (retry-now / sweep in flight) the row
    // shows a spinner instead of its failure — the ⚠️/dropdown is suppressed.
    const rowPublishing = !!rowCreative?.id && publishingIdSet.has(rowCreative.id)
    // A marker-based failure (not a provider restriction) on an unpublished
    // creative can be retried in place — wire the per-platform "Retry" into the
    // row's inline dropdown.
    const canRetry =
      !!rowFailure &&
      !restrictedPlatforms.includes(platform) &&
      !!rowCreative?.id &&
      !rowCreative.publishedAt
    return (
      <PostModalPlatformRow
        key={platform}
        platform={platform}
        isPreviewing={viewing === platform}
        isSelected={selected.has(platform)}
        isConnected={connections[platform]?.connected ?? true}
        isConnecting={connectingPlatform === platform}
        onPreview={() => handleSelectViewing(platform)}
        onToggleSelect={() => handleToggleSelect(platform)}
        disabledReason={disabledReason}
        locked={isLocked || isReadOnly}
        readOnly={isReadOnly}
        isPublished={
          !!rowCreative?.publishedAt || idea.platformResults?.[platform]?.state === 'success'
        }
        failure={rowPublishing ? undefined : rowFailure}
        isPublishing={rowPublishing}
        onRetry={
          canRetry && rowCreative ? () => handleRetryPlatform(platform, rowCreative.id) : undefined
        }
        isCustomized={
          platform !== idea?.primaryPlatform && !!siblingsState?.byPlatform[platform]?.pinnedAt
        }
      />
    )
  })

  // Bring-your-own-content: the calendar and this modal must read honestly —
  // this media is the user's own and publishes untouched. Quiet line, not a
  // warning; it also explains why "New take" and pixel edits are absent.
  const isByoPost = isUserMediaContent(
    idea.primaryCreative?.content as Record<string, unknown> | null | undefined
  )
  const byoMarker = isByoPost ? (
    <div className="flex items-center gap-1.5 px-3 py-2 text-[11px] leading-snug text-paper-faint">
      <Upload size={12} className="shrink-0" />
      Your upload. Publishes untouched.
    </div>
  ) : null

  const whenControls =
    isSynthetic || isReadOnly ? null : (
      <div className="flex gap-2">
        <Input
          variant="dark"
          type="date"
          value={date}
          min={todayInTz(scheduleTz)}
          onChange={e => setDate(e.target.value)}
          className="!h-9 w-[150px] cursor-pointer !px-3"
        />
        <Select value={time} onValueChange={setTime} disabled={availableTimeOptions.length === 0}>
          <SelectTrigger variant="dark" className="!h-9 w-[140px] cursor-pointer">
            {availableTimeOptions.length === 0 ? (
              <span className="text-paper-faint">No times left</span>
            ) : (
              <SelectValue />
            )}
          </SelectTrigger>
          <SelectContent variant="dark" className="max-h-[260px]" hideScrollButtons>
            {availableTimeOptions.map(t => (
              <SelectItem key={t} value={t}>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={10} /> {formatTime(t)}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )

  // Wrapped in motion.div sharing `layoutId` with PostPublishPanel. When
  // phase flips to 'publishing', this unmounts and the panel mounts at the
  // same layoutId — Framer Motion morphs the box between them. When phase
  // is 'scheduled', the same layoutId hosts a brief celebratory pill
  // (acid bg + check + "Scheduled for {date}") for ~1.2s before close.
  const successDate = scheduledAtForSuccess
    ? formatScheduleSummary(
        new Date(scheduledAtForSuccess).toISOString().split('T')[0]!,
        scheduledAtForSuccess.split('T')[1]!.slice(0, 5)
      )
    : ''
  // Desktop-only "Post now" caret beside the Schedule CTA — the Gmail-style
  // secondary action. Hidden for live/publishing, awaiting-approval, and
  // synthetic ideas (those have their own CTAs / already post immediately).
  // Mobile stays single-button. Note: NOT gated on availableTimeOptions —
  // "Post now" is exactly the escape hatch when no schedule slots are left.
  const showPostNowCaret =
    !isMobile && !isReadOnly && !needsApproval && !isSynthetic && !generationNotReady
  const submitDisabled =
    submitting ||
    selected.size === 0 ||
    generationNotReady ||
    (!isSynthetic && availableTimeOptions.length === 0)
  const scheduleSubmitButton = (
    <Button
      type="button"
      onClick={() => void handleSubmit()}
      disabled={submitDisabled}
      variant="t3-primary"
      size="t3-md"
      className={cn(
        showPostNowCaret ? 'w-full justify-start rounded-r-none' : isMobile && 'w-full'
      )}
    >
      {submitting ? (
        <Loader2 size={14} className="animate-spin" />
      ) : showPostNowCaret && !isSynthetic ? (
        <Calendar size={14} />
      ) : (
        <Send size={14} />
      )}
      {submitLabel}
    </Button>
  )
  const submitCta = (
    <div className={cn(isMobile && 'w-full')}>
      <AnimatePresence mode="popLayout" initial={false}>
        {phase === 'select' && (
          <motion.div
            key="cta"
            layoutId={POST_PUBLISH_PANEL_LAYOUT_ID}
            layout="position"
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            className={isMobile ? 'w-full' : ''}
          >
            {showPostNowCaret ? (
              <PostNowMenu
                side="top"
                onPostNow={() => setShowPostNowConfirm(true)}
                variant="t3-primary"
                size="t3-md"
                disabled={submitting || selected.size === 0}
                primary={scheduleSubmitButton}
              />
            ) : (
              scheduleSubmitButton
            )}
          </motion.div>
        )}
        {phase === 'scheduled' && (
          <motion.div
            key="scheduled"
            layoutId={POST_PUBLISH_PANEL_LAYOUT_ID}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            className={cn(
              'text-ink-on-acid mono inline-flex items-center gap-2 rounded-[8px] bg-acid px-4 py-2 text-[13px] font-semibold tracking-[0.04em]',
              'shadow-[0_0_24px_var(--acid-soft)]',
              isMobile && 'w-full justify-center'
            )}
          >
            <motion.span
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.08, type: 'spring', stiffness: 500, damping: 18 }}
              className="inline-flex"
            >
              <Check size={14} strokeWidth={3} />
            </motion.span>
            Scheduled for {successDate}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )

  // Single acid "Approve · {date}" CTA for a scheduled post still awaiting
  // approval (the composer routes its approve here too). The date/time
  // pickers above handle rescheduling; this one button commits the (possibly
  // changed) schedule, the platform selection and approval together, and
  // names the platform count when more than one will publish. Replaces the
  // reschedule CTA entirely in this state.
  const approveCta = needsApproval ? (
    <Button
      type="button"
      variant="t3-primary"
      size="t3-md"
      onClick={handleApproveSubmit}
      disabled={approving || selected.size === 0 || availableTimeOptions.length === 0}
      title="Approve: publishes to every selected platform at the scheduled time"
      className={isMobile ? 'w-full' : ''}
    >
      {approving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
      {approving && connectingPlatform
        ? `Connecting ${PLATFORM_LABEL[connectingPlatform]}…`
        : availableTimeOptions.length === 0
          ? 'No times left, pick another day'
          : approveCtaLabel(selected.size, formatScheduleSummary(date, time))}
    </Button>
  ) : null

  // Tertiary "Unschedule" affordance for a committed scheduled post (not a
  // review-mode/awaiting-approval one, and not live/publishing). Pulls the post
  // off the rails back to a draft. Synthetic creatives are never scheduled here.
  const canUnschedule = idea.status === 'scheduled' && !needsApproval && !isReadOnly && !isSynthetic
  const unscheduleButton = canUnschedule ? (
    <button
      type="button"
      onClick={handleUnschedule}
      disabled={unscheduling || submitting}
      title="Move this post back to a draft"
      className={cn(
        'inline-flex cursor-pointer items-center gap-1.5 rounded-[8px] px-2 py-1.5 text-[13px] text-paper-dim transition-colors hover:text-paper disabled:cursor-default disabled:opacity-50',
        'hover:bg-[color:var(--bg-3)]',
        isMobile && 'w-full justify-center'
      )}
    >
      {unscheduling ? <Loader2 size={14} className="animate-spin" /> : <CalendarOff size={14} />}
      Unschedule
    </button>
  ) : null

  // Download the post's media (image / video / carousel slides) — repeated
  // user request (support round 15, Irv). Available in EVERY state including
  // live; same subscription gate as publish (trials count). Prefers the
  // platform tab the user is viewing, falls back to the primary creative.
  // Strictly the tab the user is LOOKING at: while a non-primary sibling is
  // still hydrating, hide the button rather than silently exporting the
  // primary's (possibly different) media.
  const downloadSource = viewing ? viewingCreative : (idea.primaryCreative ?? null)
  const downloadMedia = collectDownloadableMedia(downloadSource?.content)
  const handleDownloadMedia = async () => {
    if (!gate('download')) return
    track(AnalyticsEvents.Composer.COMPOSER_DOWNLOAD_TAPPED, {
      creative_id: downloadSource?.id,
      creative_type: viewing ?? idea.primaryPlatform,
      format: downloadMedia.some(m => m.kind === 'video') ? 'video' : 'image',
      source: 'post_modal',
    })
    setDownloadingMedia(true)
    try {
      await downloadCreativeMedia(downloadSource?.name ?? 'taka_post', downloadMedia)
    } catch (error) {
      console.error('Download failed:', error)
      toast.error("Couldn't download the media. Try again.")
    } finally {
      setDownloadingMedia(false)
    }
  }
  const downloadButton =
    downloadMedia.length > 0 ? (
      <button
        type="button"
        data-testid="post-modal-download"
        onClick={() => void handleDownloadMedia()}
        disabled={downloadingMedia}
        title="Download this post's media"
        className={cn(
          'inline-flex cursor-pointer items-center gap-1.5 rounded-[8px] px-2 py-1.5 text-[13px] text-paper-dim transition-colors hover:text-paper disabled:cursor-default disabled:opacity-50',
          'hover:bg-[color:var(--bg-3)]',
          isMobile && 'w-full justify-center'
        )}
      >
        {downloadingMedia ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        Download
      </button>
    ) : null

  // Read-only footer for live/publishing posts. Live = the published
  // permalink(s) ("links only" — no edit, no retry). Publishing = a transient
  // note while the publish fans out.
  const liveResultEntries = Object.entries(idea.platformResults ?? {}) as Array<
    [LivePlatform, NonNullable<PostIdea['platformResults']>[LivePlatform]]
  >
  const liveSuccessLinks = liveResultEntries.filter(
    ([, r]) => r?.state === 'success' && !!r?.permalink
  )
  const readOnlyFooter = isLive ? (
    <div className="flex w-full flex-col gap-2">
      {downloadButton && (
        <div className={cn('flex', isMobile ? 'w-full' : 'justify-start')}>{downloadButton}</div>
      )}
      {liveSuccessLinks.length > 0 ? (
        <div className={cn('flex flex-wrap items-center gap-2', isMobile && 'w-full')}>
          {liveSuccessLinks.map(([platform, r]) => (
            <Button
              key={platform}
              asChild
              variant="t3-primary"
              size="t3-md"
              className={isMobile ? 'flex-1' : ''}
            >
              <a href={r!.permalink!} target="_blank" rel="noopener noreferrer">
                <PlatformIcon channel={platform} size={18} />
                View on {platformLabel(platform)}
                <ExternalLink size={14} />
              </a>
            </Button>
          ))}
        </div>
      ) : (
        <span className="text-[13px] text-paper-dim">
          {anyPublishFailed
            ? 'Live on some platforms. Retry the failed ones from the platform list.'
            : 'This post is live.'}
        </span>
      )}
    </div>
  ) : isPublishing ? (
    <div className="inline-flex items-center gap-2 text-[13px] text-paper-dim">
      <Loader2 size={14} className="animate-spin" /> Publishing…
    </div>
  ) : (
    // anyPublishFailed and nothing live yet — the failure rows above carry the
    // reason + Retry/Reconnect; the footer just states the post's condition.
    <span className="text-[13px] text-[color:var(--alert-error-text)]">
      This post couldn&rsquo;t publish. Fix or retry it from the platform list.
    </span>
  )

  // Morphing publish panel — absolute-positioned sibling so it overlays the
  // preview content without disturbing the body's flex layout. Shares
  // `layoutId` with the footer CTA; Framer interpolates between them.
  const morphPanel = (
    <AnimatePresence mode="popLayout">
      {phase === 'publishing' && idea && (
        <PostPublishPanel
          key="panel"
          idea={idea}
          targets={publishTargets}
          onDone={handleProgressDone}
          onRetryFailures={handleProgressRetry}
        />
      )}
    </AnimatePresence>
  )

  // Post-now confirmation — posting is irreversible (no Unschedule), so the
  // caret "Post now" routes through this before the publish fans out. Shared by
  // the footer caret and the composer's `intent='post_now'` auto-open. When the
  // post already holds a future slot the dialog says so (the 2026-08-31 early
  // publishes were users who thought this confirmed their schedule), and the
  // safe answer keeps focus so an Enter or a reflex click leaves the slot alone.
  const platformCountLabel = `${selected.size} platform${selected.size === 1 ? '' : 's'}`
  // TikTok's required per-post choices. Opened by the submit paths above when
  // TikTok is selected and its creative has none saved; the CTA re-enters the
  // same path with the choices held in `pendingTikTokSettingsRef`.
  const tiktokSettingsDialog = tiktokPrompt && (
    <TikTokPostDialog
      open
      onOpenChange={open => {
        if (!open) setTiktokPrompt(null)
      }}
      content={tiktokDialogContent()}
      isPublishing={false}
      publishError={null}
      ctaLabel={tiktokPrompt === 'schedule' ? 'Save and schedule' : 'Post now'}
      onPost={settings => {
        pendingTikTokSettingsRef.current = settings
        const mode = tiktokPrompt
        setTiktokPrompt(null)
        if (mode === 'schedule') void handleSubmit({ reentry: true })
        else void handlePostNow({ reentry: true })
      }}
    />
  )
  const postNowConfirmDialog = (
    <Dialog open={showPostNowConfirm} onOpenChange={setShowPostNowConfirm}>
      <DialogContent variant="dark" size="sm" withCloseButton>
        <DialogHeader>
          <DialogTitle variant="dark">
            {existingSchedule ? 'Post now, skipping the schedule?' : 'Post now?'}
          </DialogTitle>
          <DialogDescription variant="dark">
            {existingSchedule
              ? `This post is scheduled for ${existingSchedule}. Posting now publishes it to ${platformCountLabel} right away, takes it off that slot, and can’t be undone.`
              : `This publishes to ${platformCountLabel} immediately and can’t be undone.`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="t3-tertiary"
            size="t3-md"
            autoFocus
            onClick={() => setShowPostNowConfirm(false)}
          >
            {existingSchedule ? 'Keep the schedule' : 'Cancel'}
          </Button>
          <Button
            type="button"
            variant="t3-primary"
            size="t3-md"
            disabled={submitting || selected.size === 0}
            onClick={() => {
              setShowPostNowConfirm(false)
              void handlePostNow()
            }}
          >
            <Send size={14} />
            Post now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )

  if (isMobile) {
    // Mobile: platforms strip pinned at top, preview fills the middle and
    // scrolls inside, footer (When + Post) lives in the sheet's pinned
    // footer slot so the CTA is always visible without scrolling.
    const mobileBody = (
      <div
        className={cn(
          'relative flex h-full min-h-0 flex-col',
          phase === 'publishing' && 'pointer-events-none'
        )}
        aria-busy={phase === 'publishing'}
      >
        <div className="shrink-0 border-b border-line bg-bg-2">
          <div ref={mobileStripRef} className="flex gap-1 overflow-x-auto p-2">
            {platformRows}
          </div>
          {byoMarker}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {viewing && (
            <PostModalPreviewPane
              viewing={viewing}
              creative={viewingCreative}
              direction={direction}
              onEdit={isReadOnly ? undefined : handleEditPlatform}
              onNewTake={canNewTake ? handleNewTake : undefined}
              shape={idea?.shape}
            />
          )}
        </div>
        {morphPanel}
      </div>
    )

    const mobileFooter = (
      <div className="flex flex-col gap-3 bg-bg-2 px-4 py-3">
        {blockedNotice}
        {scheduleError && (
          <div
            role="alert"
            className="rounded-[8px] border border-line-bright bg-bg-3 px-3 py-2 text-[12px] text-[color:var(--alert-error-text)]"
          >
            {scheduleError}
          </div>
        )}
        {isReadOnly ? (
          readOnlyFooter
        ) : (
          <>
            {whenControls}
            {needsApproval ? approveCta : submitCta}
            {unscheduleButton}
            {downloadButton}
          </>
        )}
      </div>
    )

    return (
      <>
        {chooser}
        <MobileBottomSheet open={!!idea} onClose={onClose} title={modalTitle} footer={mobileFooter}>
          {mobileBody}
        </MobileBottomSheet>
        {postNowConfirmDialog}
        {tiktokSettingsDialog}
      </>
    )
  }

  // ─── Desktop ───────────────────────────────────────────────────────────
  // `relative` so the absolute-positioned PostPublishPanel anchors here
  // (bottom-right corner of the body). `pointer-events-none` while the
  // panel is up locks the platform list + preview without dimming them.
  const desktopBody = (
    <div
      className={cn(
        'relative flex min-h-0 flex-1 flex-col',
        phase === 'publishing' && 'pointer-events-none'
      )}
      aria-busy={phase === 'publishing'}
    >
      <div className="flex min-h-0 flex-1 flex-row">
        <div className="w-[220px] shrink-0 border-r border-line bg-bg-2">
          {/* Read-only posts can't choose anything — the label is a noun, not a CTA. */}
          <div className="px-3 pt-4 pb-2 text-[13px] text-paper-dim">
            {isReadOnly ? 'Platforms' : 'Choose where to post'}
          </div>
          <div className="flex flex-col gap-1 overflow-y-auto p-2">{platformRows}</div>
          {byoMarker}
        </div>

        <div className="min-h-0 flex-1 p-4">
          {viewing && (
            <PostModalPreviewPane
              viewing={viewing}
              creative={viewingCreative}
              direction={direction}
              onEdit={isReadOnly ? undefined : handleEditPlatform}
              onNewTake={canNewTake ? handleNewTake : undefined}
              shape={idea?.shape}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-line bg-bg-2 px-4 py-3">
        {blockedNotice}
        {scheduleError && (
          <div
            role="alert"
            className="rounded-[8px] border border-line-bright bg-bg-3 px-3 py-2 text-[12px] text-[color:var(--alert-error-text)]"
          >
            {scheduleError}
          </div>
        )}
        {isReadOnly ? (
          readOnlyFooter
        ) : (
          <div className="flex items-center justify-between gap-3">
            {whenControls ?? <div />}
            <div className="flex items-center gap-5">
              {downloadButton}
              {unscheduleButton}
              {needsApproval ? approveCta : submitCta}
            </div>
          </div>
        )}
      </div>

      {morphPanel}
    </div>
  )

  return (
    <>
      {chooser}
      <Dialog
        open={!!idea}
        onOpenChange={open => {
          // Block dismiss while the morphing publish panel is in flight — the
          // OK button inside the panel is the only valid escape until every
          // platform terminates.
          if (!open && phase === 'publishing') return
          if (!open) onClose()
        }}
      >
        <DialogContent
          variant="dark"
          size="lg"
          className="!flex !h-[85vh] !max-h-[760px] !max-w-[920px] !flex-col !gap-0 !p-0"
        >
          <DialogHeader className="shrink-0 border-b border-line px-4 py-3">
            <DialogTitle variant="dark" className="!text-left !text-sm">
              {modalTitle}
            </DialogTitle>
          </DialogHeader>
          {desktopBody}
        </DialogContent>
      </Dialog>
      {postNowConfirmDialog}
      {tiktokSettingsDialog}
    </>
  )
}
